import React, {Component} from 'react';
import axios from 'axios';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import Chip from '@material-ui/core/Chip';
import Auth from '../../../auth/auth';

// ponytail: self-contained widget (own axios + local state), matching ClassificationPanel,
// OwnershipPanel and DeclassificationEffect. dd-details-view is not connect()ed — it takes props
// from the connected parent — so the redux route would cost three files of plumbing for one GET.
const API = process.env.REACT_APP_API_URL || '';

const LABELS = {internal: 'Internal', confidential: 'Confidential'};

// Two names then "+N more". A confidential row visible to many — every legacy_super holder plus
// auditors, which on seeded dev is everyone — would otherwise render an unbounded name list inside
// a table cell. The full answer is one click away in the who-can-see panel, which is the screen
// built for it.
const NAMES_IN_CELL = 2;

/**
 * Mockup screen S4's table — `Table / Owner / Classification / Visible to`.
 *
 * ADDITIVE, beside the existing card grid rather than replacing it. `dd-details-view` renders one
 * `DDItemCard` per object; porting the mockup's table over that would duplicate the card's data
 * source and create a second edit surface for the same tables.
 *
 * "OWNER" MEANS THE OWNER WHOSE SIGHT TRAVELS WITH OWNERSHIP — the access-model owner — and NOT
 * `dd_object_registry`'s `businessOwner`/`techOwner` metadata, which the object payload already
 * carries. Those are stewardship labels that confer nothing; putting one in a column whose
 * neighbour is "Visible to" would print a name beside an audience it has no part in.
 */
class ObjectVisibilityTable extends Component {
  constructor(props) {
    super(props);
    this.state = {sight: null, error: '', loading: false};
  }

  componentDidMount() {
    this.load();
  }

  // The schema can change without this unmounting when the details view switches dictionaries.
  componentDidUpdate(prev) {
    if (prev.schemaId !== this.props.schemaId) this.load();
  }

  load() {
    const {schemaId} = this.props;
    if (!schemaId) return;
    this.setState({loading: true, error: ''});
    axios.get(`${API}/access/schema-object-sight/${schemaId}`, {
      headers: {'Accept': 'application/json', 'x-access-token': new Auth().getAccessToken()},
    })
        .then((res) => this.setState({sight: res.data, loading: false}))
        .catch((err) => this.setState({loading: false,
          error: String((err.response && err.response.data && err.response.data.error) || err.message || err)}));
  }

  /**
   * Whether this row's owner is missing from its own audience.
   *
   * THE SERVER DECIDES THIS, on subject ids. An earlier version compared display names here and was
   * wrong in one direction that mattered: owners come from `data_ownership`, where an owner can be a
   * ROLE, while `visibleTo` holds user display names only — so every role-owned confidential table
   * reported "the owner cannot see it", a false statement on the marker added to prevent exactly
   * that class of false statement.
   *
   * `null` means unanswerable (no user owner), and is not the same as `false`.
   *
   * @param {object} row One object from the sight read.
   * @return {boolean} True only when a USER owner genuinely cannot see the table they own.
   */
  ownerCannotSee(row) {
    return row.ownerCanSee === false;
  }

  /**
   * Bring the schema's OwnershipPanel into view — the one `dd-details-view` already renders in this
   * card, never a second copy of it.
   *
   * Found by `data-entity-id` rather than a ref because this component is not the panel's parent:
   * they are siblings under `dd-details-view`, which is not `connect()`ed and threads props down.
   * A ref would mean plumbing a callback through a component that exists to stay dumb.
   *
   * @return {void}
   */
  focusOwnershipPanel = () => {
    const panel = document.querySelector(
        `[data-testid="dd-ownership-panel"][data-entity-id="${this.props.schemaId}"]`);
    if (!panel) return;
    panel.scrollIntoView({behavior: 'smooth', block: 'center'});
    const input = panel.querySelector('[data-testid="dd-ownership-assign-input"]');
    // Only when the viewer may actually edit — OwnershipPanel renders the picker behind `canEdit`,
    // so its absence means "you cannot assign here", and focusing nothing is the right no-op.
    if (input) input.focus();
  };

  renderVisibleTo(row) {
    // An internal table is readable by anyone who can read the schema. That is a sentence, not a
    // list — and rendering per-person rows for it would print the whole tenancy in a table cell.
    if (row.classification !== 'confidential') {
      return <span className="vis-everyone" data-testid="dd-visible-everyone">Everyone</span>;
    }

    const names = row.visibleTo || [];
    if (!names.length) {
      return <span className="vis-none">Owners only</span>;
    }
    const shown = names.slice(0, NAMES_IN_CELL).join(' · ');
    const rest = names.length - NAMES_IN_CELL;
    return (
      <span>
        {shown}
        {rest > 0 && <span className="vis-more"> + {rest} more</span>}
      </span>
    );
  }

  render() {
    const {onClassify} = this.props;
    const {sight, error} = this.state;

    if (error) {
      return <div className="visibility-error" data-testid="dd-visibility-error">{error}</div>;
    }
    if (!sight || !sight.objects) return null;

    return (
      <div className="object-visibility" data-testid="dd-object-visibility-table">
        <div className="visibility-head">
          <span className="visibility-count">
            {sight.objects.length} {sight.objects.length === 1 ? 'table' : 'tables'}
          </span>
          {sight.confidentialCount > 0 && (
            <Chip size="small" className="confidential-count" data-testid="dd-confidential-count"
              /* THE COUNT IS OF ROWS THE VIEWER CAN SEE. "2 confidential" above rows the §7.1
                 filter removed would be the leak that caption forbids, so when the filter withheld
                 anything the label says so rather than implying a total. */
              label={sight.withheld ?
                `🔒 ${sight.confidentialCount} confidential you can see` :
                `🔒 ${sight.confidentialCount} confidential`} />
          )}
          {/* Ponytail rung 1, arrived at the long way. The tooltip this replaced claimed assigning
              owners needed a granting mechanism that "is not built yet" — untrue: POST
              /userProfile/ownership exists, runs under assertNoSelfWiden, and OwnershipPanel
              already drives it.

              But the first fix was worse than the problem. It opened that panel in a DRAWER, while
              `dd-details-view` already renders the very same OwnershipPanel for the very same
              schema EIGHTEEN LINES ABOVE this table, inside this same "Schema Access" card. Two
              mounted copies with independent local state: assigning an owner in the drawer left the
              copy on the page stale, and this component's onClose reloads the SIGHT table, not its
              sibling. A second edit surface for one resource is the thing the card was built to
              avoid.

              So the affordance scrolls to the panel that is already there. The mockup draws a
              "Manage owners" control and this is one; what it must not be is a duplicate. */}
          <button type="button" className="link-button" data-testid="dd-manage-owners"
            onClick={this.focusOwnershipPanel}>Manage owners</button>
        </div>

        <Table size="small" className="data-table visibility-table">
          <TableHead>
            <TableRow>
              <TableCell><strong>Table</strong></TableCell>
              <TableCell><strong>Owner</strong></TableCell>
              <TableCell><strong>Classification</strong></TableCell>
              <TableCell><strong>Visible to</strong></TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {sight.objects.map((row) => (
              <TableRow key={row.objectId} hover data-testid="dd-object-row"
                data-object-id={row.objectId}>
                <TableCell>
                  {row.classification === 'confidential' && <span className="lock">🔒 </span>}
                  {row.objectName}
                </TableCell>
                <TableCell data-testid="dd-object-owner" data-owner-level={row.ownerLevel || 'none'}>
                  {/* Ownership is schema-level in practice — OwnershipPanel records it and
                      dd-details-view only ever mounts it with entityType="schema" — so most rows
                      show the same owner. The read checks for an OBJECT-level row first and falls
                      back, and says which it used: rendering a schema owner as if it were the
                      table's own would be a quiet fiction about the thing the next column depends
                      on. The mockup's per-row variation is not fabricated when the data cannot
                      produce it. */}
                  {row.owners && row.owners.length ? (
                    <>
                      {row.owners.join(' + ')}
                      {row.ownerLevel === 'schema' && (
                        <span className="owner-inherited"> (schema)</span>
                      )}
                      {/* THE CONTRADICTION THE VISUAL PASS FOUND, turned into the finding it
                          actually is. On seeded data the Owner column read "E2E Admin" while
                          "Visible to" read "E2E Steward" — the owner absent from the audience for
                          their own confidential table. Both cells are truthful and come from
                          different places: ownership from `data_ownership`, sight from `ac_grant`.
                          Side by side with no explanation they read as a bug in the screen; said
                          out loud, they are a real access-model finding a data owner needs. */}
                      {this.ownerCannotSee(row) && (
                        <span className="owner-blind" data-testid="dd-owner-blind"
                          title="This owner holds no confidential sight here, so they cannot see this table">
                          {' '}— cannot see it
                        </span>
                      )}
                    </>
                  ) : <span className="vis-none">No owner</span>}
                </TableCell>
                <TableCell>
                  <Chip size="small" data-testid="dd-object-classification"
                    label={LABELS[row.classification] || row.classification}
                    className={row.classification === 'confidential' ? 'chip-confidential' : 'chip-internal'} />
                  {row.inheritedFromSchema && (
                    <span className="inherited" data-testid="dd-inherited">
                      {' '}via schema
                    </span>
                  )}
                </TableCell>
                <TableCell data-testid="dd-object-visible-to">{this.renderVisibleTo(row)}</TableCell>
                <TableCell>
                  <button type="button" className="link-button"
                    onClick={() => onClassify && onClassify(row)}
                    data-testid="dd-object-classify">Classify</button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  }
}

export default ObjectVisibilityTable;
